import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { RaidPracticeDeckDisplay } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';

function numberValue(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatEok(value) {
  return `${numberValue(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function itemLabel(item = {}) {
  return String(item.title || '').trim() || `모의전 ${numberValue(item.slotNo) || 1}`;
}

function signed(delta, format = String) {
  if (Math.abs(delta) < 1e-9) return '0';
  return `${delta > 0 ? '+' : '-'}${format(Math.abs(delta))}`;
}

// Whether a metric's change is good for the union: more cleared/damage is better, while more
// overkill (wasted damage) or more attacks spent is worse.
function deltaTone(delta, higherIsBetter) {
  if (Math.abs(delta) < 1e-9) return 'same';
  return (delta > 0) === higherIsBetter ? 'better' : 'worse';
}

function verdictReason(result, originalNickname) {
  switch (result.decidedBy) {
    case 'cleared':
      return `처치 가능한 보스가 ${Math.abs(result.clearedDelta)}개 ${result.clearedDelta > 0 ? '늘어납니다' : '줄어듭니다'}.`;
    case 'overkill':
      return `처치 수는 같고, 보스를 잡고 남아 버려지는 초과딜이 ${formatRaidHpEok(Math.abs(result.overkillDelta))} ${result.overkillDelta < 0 ? '줄어듭니다' : '늘어납니다'}.`;
    case 'hits':
      return `처치 수와 초과딜은 같고, 필요한 타수가 ${Math.abs(result.usedHitsDelta)}타 ${result.usedHitsDelta < 0 ? '줄어듭니다' : '늘어납니다'}.`;
    case 'damage':
      return `다른 지표는 모두 같고, 총딜만 ${formatEok(Math.abs(result.totalDamageDelta))} ${result.totalDamageDelta > 0 ? '늘어납니다' : '줄어듭니다'}.`;
    default:
      return `${originalNickname} 대신 써도 유니온 전체 결과는 현재 배정과 같습니다.`;
  }
}

function comboStatus(combo) {
  if (!combo.cleared) return { tone: 'short', text: `부족 ${formatRaidHpEok(combo.remaining)}` };
  if (combo.overkill > 1e-9) return { tone: 'over', text: `초과 ${formatRaidHpEok(combo.overkill)}` };
  return { tone: 'exact', text: '딱 맞게 처치' };
}

function replanReason({ before, after }) {
  if (!after.cleared) return `교체 후에는 남은 후보로 이 보스를 다 채우지 못해 ${formatRaidHpEok(after.remaining)}이 부족합니다.`;
  if (after.items.length < before.items.length) {
    return `필요한 타수가 ${before.items.length}타에서 ${after.items.length}타로 줄어, 빠진 인원의 타수는 다른 보스에 쓸 수 있습니다.`;
  }
  if (after.items.length > before.items.length) {
    return `교체 후보가 기존보다 약해 이 보스를 처치하려면 ${after.items.length}타가 필요합니다(기존 ${before.items.length}타).`;
  }
  return `같은 ${after.items.length}타로 처치하며, 나머지 인원은 다시 계산해 가장 알맞은 조합으로 채웠습니다.`;
}

function ComboColumn({ label, combo, pinnedId = '', className = '' }) {
  const status = comboStatus(combo);
  return (
    <article className={`raid-tetris-tradeoff-combo ${className}`.trim()}>
      <header>
        <span>{label}</span>
        <strong>{combo.items.length}타 · {formatEok(combo.total)}</strong>
        <small className={status.tone}>{status.text}</small>
      </header>
      <ol>
        {combo.items.map((item) => (
          <li key={item.id} className={item.id === pinnedId ? 'pinned' : ''}>
            <span><strong>{item.nickname}</strong><small>{item.id === pinnedId ? '교체 후보' : itemLabel(item)}</small></span>
            <b>{formatEok(item.damage ?? item.damageEok)}</b>
          </li>
        ))}
      </ol>
    </article>
  );
}

function SlotSummary({ item, mobile }) {
  return (
    <div className="raid-tetris-tradeoff-slot-summary">
      <div className="raid-tetris-tradeoff-slot-name">
        <strong>{item.nickname}</strong>
        <small>{itemLabel(item)}</small>
      </div>
      <b>{formatEok(item.damage ?? item.damageEok)}</b>
      <RaidPracticeDeckDisplay deck={item.deck} mobile={mobile} />
    </div>
  );
}

export function RaidTetrisFullTradeoffDialog({
  mobile = false,
  target = null,
  originalItem = null,
  candidates = [],
  tradeoff = null,
  onPick = () => {},
  onBack = () => {},
  onClose = () => {},
}) {
  const open = Boolean(tradeoff && target && originalItem);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const { status, alternate, result, error } = tradeoff;
  // 총딜 = 유효딜 + 초과딜. Showing 유효딜 separately (and leaving 총딜 uncoloured) makes it visible
  // when a total-damage change is nothing but wasted overkill appearing or disappearing — otherwise a
  // swap that cuts waste shows a "worse" red drop in total damage right next to a "better" overkill.
  const effectiveBaseline = result?.baseline ? result.baseline.totalDamage - result.baseline.totalOverkill : 0;
  const effectiveScenario = result?.scenario ? result.scenario.totalDamage - result.scenario.totalOverkill : 0;
  const metrics = result?.baseline && result?.scenario ? [
    { label: '처치', baseline: `${result.baseline.cleared}개`, scenario: `${result.scenario.cleared}개`, delta: signed(result.clearedDelta, (value) => `${value}개`), tone: deltaTone(result.clearedDelta, true) },
    { label: '사용 타수', baseline: `${result.baseline.usedHits}타`, scenario: `${result.scenario.usedHits}타`, delta: signed(result.usedHitsDelta, (value) => `${value}타`), tone: deltaTone(result.usedHitsDelta, false) },
    { label: '유효딜', baseline: formatEok(effectiveBaseline), scenario: formatEok(effectiveScenario), delta: signed(effectiveScenario - effectiveBaseline, formatEok), tone: deltaTone(effectiveScenario - effectiveBaseline, true) },
    { label: '초과딜', baseline: formatRaidHpEok(result.baseline.totalOverkill), scenario: formatRaidHpEok(result.scenario.totalOverkill), delta: signed(result.overkillDelta, formatRaidHpEok), tone: deltaTone(result.overkillDelta, false) },
    { label: '총딜', baseline: formatEok(result.baseline.totalDamage), scenario: formatEok(result.scenario.totalDamage), delta: signed(result.totalDamageDelta, formatEok), tone: 'neutral' },
  ] : [];

  const dialog = (
    <div className={`raid-tetris-tradeoff-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="raid-tetris-tradeoff-dialog" role="dialog" aria-modal="true" aria-labelledby="raid-tetris-tradeoff-title">
        <header className="raid-tetris-tradeoff-head">
          <div>
            <span>배정 비교</span>
            <strong id="raid-tetris-tradeoff-title">{target.level}단 {bossDisplayName(target)}</strong>
          </div>
          <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
        </header>

        <div className="raid-tetris-tradeoff-body">
          <section className="raid-tetris-tradeoff-slot">
            <h4>현재 배정</h4>
            <SlotSummary item={originalItem} mobile={mobile} />
          </section>

          {status === 'picking' ? (
            <section className="raid-tetris-tradeoff-picker">
              <h4>{originalItem.nickname} 대신 넣어볼 후보</h4>
              <p>같은 보스에 등록됐지만 현재 계획에서 아직 쓰이지 않은 모의전입니다. 선택한 후보를 이 보스에 넣고, 이 보스의 나머지 조합(초과되면 인원을 줄이고 부족하면 더 채움)과 다른 보스 배정을 다시 계산합니다.</p>
              {candidates.length ? (
                <div className="raid-tetris-tradeoff-options">
                  {candidates.map((candidate) => (
                    <button type="button" key={candidate.id} onClick={() => onPick(candidate)}>
                      <SlotSummary item={candidate} mobile={mobile} />
                    </button>
                  ))}
                </div>
              ) : <p className="raid-tetris-tradeoff-empty">비교할 수 있는 다른 후보가 없습니다.</p>}
            </section>
          ) : status === 'loading' ? (
            <p className="raid-tetris-tradeoff-state" role="status" aria-live="polite">
              {originalItem.nickname} → {alternate?.nickname} 으로 바꿨을 때를 계산하는 중입니다…
            </p>
          ) : status === 'error' ? (
            <div className="raid-tetris-tradeoff-state error">
              <p>{error}</p>
              <button type="button" onClick={onBack}>다른 후보 고르기</button>
            </div>
          ) : (
            <>
              <section className="raid-tetris-tradeoff-slot alternate">
                <h4>교체 후보</h4>
                <SlotSummary item={alternate} mobile={mobile} />
              </section>

              <section className={`raid-tetris-tradeoff-verdict ${result.verdict}`}>
                <strong>{result.verdict === 'gain' ? '교체하면 이득' : result.verdict === 'loss' ? '교체하면 손해' : '결과 동일'}</strong>
                <p>{verdictReason(result, originalItem.nickname)}</p>
              </section>

              {result.swappedTarget ? (
                <section className="raid-tetris-tradeoff-replan">
                  <h4>이 보스 재계산 결과</h4>
                  <div className="raid-tetris-tradeoff-replan-grid">
                    <ComboColumn label="현재" combo={result.swappedTarget.before} />
                    <ComboColumn label="교체 후" combo={result.swappedTarget.after} pinnedId={alternate?.id} className="after" />
                  </div>
                  <p>{replanReason(result.swappedTarget)}</p>
                </section>
              ) : null}

              <div className="raid-tetris-tradeoff-metrics-wrap">
                <table className="raid-tetris-tradeoff-metrics">
                  <thead>
                    <tr><th scope="col">항목</th><th scope="col">현재</th><th scope="col">교체 시</th><th scope="col">차이</th></tr>
                  </thead>
                  <tbody>
                    {metrics.map((metric) => (
                      <tr key={metric.label}>
                        <th scope="row">{metric.label}</th>
                        <td>{metric.baseline}</td>
                        <td>{metric.scenario}</td>
                        <td className={metric.tone}>{metric.delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <small className="raid-tetris-tradeoff-metrics-note">총딜 = 유효딜 + 초과딜. 초과딜은 보스를 잡고 남아 버려지는 딜이라 적을수록 좋습니다.</small>
              </div>

              <section className="raid-tetris-tradeoff-changed">
                <h4>함께 바뀌는 보스 배정</h4>
                {result.changedTargets.length ? (
                  <>
                    <p>{originalItem.nickname}의 타수가 풀리면서 아래 보스들의 배정도 다시 계산됩니다. 총딜이 이 한 타의 딜량 차이보다 크게 변하는 이유입니다.</p>
                    <ul>
                      {result.changedTargets.map((changed) => <li key={changed.id}>{changed.level}단 {bossDisplayName(changed)}</li>)}
                    </ul>
                  </>
                ) : <p>다른 보스 배정은 바뀌지 않습니다.</p>}
              </section>
            </>
          )}
        </div>

        {status === 'done' ? (
          <footer className="raid-tetris-tradeoff-actions">
            <button type="button" onClick={onBack}>다른 후보로 비교</button>
            <button type="button" className="primary" onClick={onClose}>닫기</button>
          </footer>
        ) : null}
      </section>
    </div>
  );

  return createPortal(dialog, document.body);
}
